/**
 * Turbo uses `fetch()` for page navigations & form submissions, wrapping it with their `nativeFetch()` method.
 * When a `fetch()` fails (due to network error, timeout, CORS issue etc.) it returns a rejected Promise,
 * which if uncaught by our code, triggers a global `unhandledrejection` event.
 * This results in appsignal reporting `UnhandledPromiseRejectionError: {}` with no further info.
 *
 * This function aims to:
 *   - Catch those events
 *   - Detect if they're Turbo related from the stack trace
 *   - Sends an enhanced error which still retains the original info
 *   - Prevent the error continuing so we don't get duplicated error reports
 */
export function installUnhandledPromiseRejectionListener(appsignal) {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason?.message || '';
    const stack = reason?.stack || '';

    const looksLikeEmptyError =
      typeof reason === 'object' && !message && (!stack || stack === '');

    // If it has a stack, check for Turbo -
    // but if stack is empty, assume Turbo anyway
    const isTurboRelated =
      looksLikeEmptyError ||
      stack.includes('turbo') ||
      stack.includes('nativeFetch');

    if (isTurboRelated && looksLikeEmptyError) {
      const span = appsignal.createSpan();

      const error = new Error('Unhandled Turbo fetch rejection');
      error.name = 'UnhandledTurboFetchRejection';
      span.setError(error);

      // Guard against JSON.stringify failing
      let stringifiedReason = 'unserializable reason';
      try {
        stringifiedReason = JSON.stringify(reason);
      } catch (_) {
        // Ignore failed stringify call
      }

      const currentVisit = window.Turbo?.navigator?.currentVisit;
      const failingUrl = currentVisit?.location?.href || window.location.href;

      span.setTags({
        source: 'turbo',
        reason_type: reason?.constructor?.name || typeof reason,
      });

      span.setParams({
        original_reason: stringifiedReason,
        original_stack: stack || 'No stack trace',
        url: failingUrl,
      });

      span.setAction('UnhandledTurboFetchRejection');
      span.setNamespace('turbo-fetch');

      appsignal.send(span);

      event.preventDefault();
    }
  });
}
